import AdobeModalImage from '@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/adobe/adobe-apply-modal.webp'
import AdobeAcrobatLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/AdobeAcrobat.png'
import AdobeExpressLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/AdobeExpress.png'
import OpenAiModalImage from '@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/open-ai/open-ai-apply-modal.webp'
import OpenAiLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/Openai.png'
import ZoomModalImage from '@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/zoom/zoom-apply-modal.webp'
import ZoomLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/Zoom.webp'
import LovableModalImage from '@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/lovable/lovable-apply-modal.webp'
import LovableLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/Lovable.png'
import ClaudeModalImage from '@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/claude/claude-apply-modal.webp'
import ClaudeLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/Claude.png'
import MetaModalImage from '@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/meta/meta-apply-modal.webp'
import MetaLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/meta.png'
import { ApplyModalContainer as ApplySubmodal } from '@percent/cause-dashboard/app/productOffersDetail/applyModal/ApplyModalContainer'
import { useTranslation } from 'react-i18next'
import { useSmartApplyVariant } from '@percent/cause-dashboard/common/hooks/useSmartApplyVariant'
import { usePostHogFeatureFlag } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { useMetaOfferAllowedInCountry } from '@percent/cause-dashboard/common/hooks/useMetaOfferAllowedInCountry/useMetaOfferAllowedInCountry'
import HootsuiteLogo from '@percent/cause-dashboard/common/assets/images/partner-offers/Hootsuite.png'
import HootsuiteModalImage from '@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/hootsuite/hootsuite-apply-modal.webp'
import { SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartners'

type SmartApplyRouterProps = {
  selectedPartner: SmartApplyPartner | null
  onClose: () => void
  onSuccess: () => void
  experimentVariantOverride?: string
}

export function SmartApplyRouter({
  selectedPartner,
  onClose,
  onSuccess,
  experimentVariantOverride
}: Readonly<SmartApplyRouterProps>) {
  const { t } = useTranslation()
  const { getExperimentVariant } = useSmartApplyVariant()
  const experimentVariant = experimentVariantOverride ?? getExperimentVariant()
  const lovableSmartApply = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_LOVABLE_SMART_APPLY)
  const claudeSmartApply = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_CLAUDE_SMART_APPLY)
  const metaSmartApply = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_META_SMART_APPLY)
  const adobeExpressSmartApply = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_ADOBE_EXPRESS_SECOND_SMART_APPLY)
  const isMetaAllowedInCountry = useMetaOfferAllowedInCountry(metaSmartApply)

  if (selectedPartner === 'adobe') {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={AdobeModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.adobeAcrobatPro.modal.title')}
        modalDescription={t('typography.productOffersDetail.adobeAcrobatPro.modal.description')}
        disclaimer={t('typography.productOffersDetail.adobeAcrobatPro.modal.disclaimer')}
        products={[
          {
            name: 'adobeAcrobatPro',
            displayName: t('typography.productOffersDetail.adobeAcrobatPro.modal.productName'),
            description: t('typography.productOffersDetail.adobeAcrobatPro.modal.productDescription'),
            logo: AdobeAcrobatLogo,
            isOptional: true
          },
          {
            name: 'adobeExpress',
            displayName: t('typography.productOffersDetail.adobeExpress.modal.productName'),
            description: t('typography.productOffersDetail.adobeExpress.modal.productDescription'),
            logo: AdobeExpressLogo,
            isOptional: true
          }
        ]}
      />
    )
  }

  if (selectedPartner === 'zoom') {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={ZoomModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.zoom.modal.title')}
        modalDescription={t('typography.productOffersDetail.zoom.modal.description')}
        disclaimer={t('typography.productOffersDetail.zoom.modal.disclaimer')}
        products={[
          {
            name: 'zoom',
            displayName: t('typography.productOffersDetail.zoom.modal.productName'),
            description: t('typography.productOffersDetail.zoom.modal.productDescription'),
            logo: ZoomLogo,
            isOptional: false
          }
        ]}
      />
    )
  }

  if (selectedPartner === 'lovable' && lovableSmartApply) {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={LovableModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.lovable.modal.title')}
        modalDescription={t('typography.productOffersDetail.lovable.modal.description')}
        disclaimer={t('typography.productOffersDetail.lovable.modal.disclaimer')}
        products={[
          {
            name: 'lovable',
            displayName: t('typography.productOffersDetail.lovable.modal.productName'),
            description: t('typography.productOffersDetail.lovable.modal.productDescription'),
            logo: LovableLogo,
            isOptional: false
          }
        ]}
      />
    )
  }

  if (selectedPartner === 'claude' && claudeSmartApply) {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={ClaudeModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.claude.modal.title')}
        modalDescription={t('typography.productOffersDetail.claude.modal.description')}
        disclaimer={t('typography.productOffersDetail.claude.modal.disclaimer')}
        products={[
          {
            name: 'claude',
            displayName: t('typography.productOffersDetail.claude.modal.productName'),
            description: t('typography.productOffersDetail.claude.modal.productDescription'),
            logo: ClaudeLogo,
            isOptional: false
          }
        ]}
      />
    )
  }

  if (selectedPartner === 'adobeExpress' && adobeExpressSmartApply) {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={AdobeModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.adobeExpress.modal.title')}
        modalDescription={t('typography.productOffersDetail.adobeExpress.modal.description')}
        disclaimer={t('typography.productOffersDetail.adobeExpress.modal.disclaimer')}
        products={[
          {
            name: 'adobeExpress',
            displayName: t('typography.productOffersDetail.adobeExpress.modal.productName'),
            description: t('typography.productOffersDetail.adobeExpress.modal.productDescription'),
            logo: AdobeExpressLogo,
            isOptional: false
          }
        ]}
      />
    )
  }

  if (selectedPartner === 'openai') {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={OpenAiModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.openAi.modal.title')}
        modalDescription={t('typography.productOffersDetail.openAi.modal.description')}
        disclaimer={t('typography.productOffersDetail.openAi.modal.disclaimer')}
        products={[
          {
            name: 'openai',
            displayName: t('typography.productOffersDetail.openAi.modal.productName'),
            description: t('typography.productOffersDetail.openAi.modal.descriptionAllInOne'),
            logo: OpenAiLogo,
            isOptional: false
          }
        ]}
      />
    )
  }

  if (selectedPartner === 'meta' && metaSmartApply && isMetaAllowedInCountry) {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={MetaModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.meta.modal.title')}
        modalDescription={t('typography.productOffersDetail.meta.modal.description')}
        disclaimer=""
        externalApplyUrl="https://www.facebook.com/donate/signup/?source=goodstack_tile"
        products={[
          {
            name: 'meta',
            displayName: t('typography.productOffersDetail.meta.modal.productName'),
            description: t('typography.productOffersDetail.meta.modal.productDescription'),
            logo: MetaLogo,
            isOptional: false
          }
        ]}
      />
    )
  }

  if (selectedPartner === 'hootsuite') {
    return (
      <ApplySubmodal
        isOpen={true}
        handleClose={onClose}
        onSuccess={onSuccess}
        image={HootsuiteModalImage}
        source="smart-apply"
        partner={selectedPartner}
        experimentVariant={experimentVariant}
        modalTitle={t('typography.productOffersDetail.hootsuite.modal.title')}
        modalDescription={t('typography.productOffersDetail.hootsuite.modal.description')}
        disclaimer=""
        externalApplyUrl="https://www.hootsuite.com/pages/landing/non-profit-discount-application-percent?utm_campaign=hootgiving&utm_medium=content_syndication&utm_source=sponsored&utm_partner=goodstack&utm_vertical=non-profit&utm_content=may_homepage_tile"
        products={[
          {
            name: 'hootsuite',
            displayName: t('typography.productOffersDetail.hootsuite.modal.productName'),
            description: t('typography.productOffersDetail.hootsuite.modal.productDescription'),
            logo: HootsuiteLogo,
            isOptional: false
          }
        ]}
      />
    )
  }

  return null
}
